import mapboxgl from "mapbox-gl";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import { AppContext, AppFeature, Tab } from "./AppContext";
import { Map } from "./Map";
import Search from "./Search";
import { VenuePopup } from "./VenuePopup";
import { toGeoJSON } from "@mapbox/polyline";
import { MAPBOX_API_KEY } from "../utils/mapbox-api-key";
import { venueToCardStyle } from "../utils/venue-style-mapping";
import { Legend } from "./Legend";

export const MapController = () => {
  const {
    setMapViewport,
    venues,
    filteredVenues,
    tourVenues,
    selectedVenue,
    setSelectedVenue,
    setVenueOptions,
    route,
    isFilterMapMove,
    setIsFilterMapMove,
    setMobileToggle,
    setShowFilters,
  } = useContext(AppContext);
  const mapEl = useRef<Map>(null);

  const routeGeojson = useMemo(() => {
    if (route && route.geometry) {
      const fc = [
        { id: "route", type: "Feature", geometry: toGeoJSON(route.geometry) },
      ];
      return fc;
    }

    return [];
  }, [route]);

  useEffect(() => {
    if (mapEl.current && mapEl.current.map && selectedVenue) {
      const bounds = mapEl.current.map.getBounds();
      const center = bounds.getCenter();

      const latOffset = center.lat - selectedVenue.geometry.coordinates[1];
      const lngOffset = center.lng - selectedVenue.geometry.coordinates[0];

      const newNorthEast = bounds.getNorthEast();
      newNorthEast.lat -= latOffset;
      newNorthEast.lng -= lngOffset;
      bounds.setNorthEast(newNorthEast);

      const newSouthWest = bounds.getSouthWest();
      newSouthWest.lat -= latOffset;
      newSouthWest.lng -= lngOffset;
      bounds.setSouthWest(newSouthWest);

      const newCameraTransform = mapEl.current.map.cameraForBounds(bounds, {
        offset: [0, 100],
      });

      if (newCameraTransform) {
        mapEl.current.map.easeTo(newCameraTransform);
      }
    }
  }, [mapEl, selectedVenue]);

  useEffect(() => {
    if (mapEl.current && mapEl.current.map && route) {
      // determine bounds
      const geometry = toGeoJSON(route.geometry);
      // Create a 'LngLatBounds' with both corners at the first coordinate.
      const bounds = new mapboxgl.LngLatBounds(
        geometry.coordinates[0] as [number, number],
        geometry.coordinates[0] as [number, number]
      );

      // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
      for (const coord of geometry.coordinates) {
        bounds.extend(coord as [number, number]);
      }

      mapEl.current.map.fitBounds(bounds, {
        padding: 80,
        maxDuration: 5000, // @sbk23 - tweak here
      });
    }
  }, [mapEl, route]);

  const popups = useMemo(() => {
    if (selectedVenue) {
      const { backgroundColor } = venueToCardStyle(selectedVenue);
      return [
        {
          tipColor: backgroundColor,
          latitude: selectedVenue.geometry.coordinates[1],
          longitude: selectedVenue.geometry.coordinates[0],
          children: <VenuePopup venue={selectedVenue} />,
          options: {
            anchor: "bottom",
            maxWidth: "280px",
            closeButton: false,
            closeOnClick: false,
            className: "rtn-mapbox-popup",
          } as mapboxgl.PopupOptions,
        },
      ];
    }

    return [];
  }, [selectedVenue]);

  const visibleLayers: { [k: string]: boolean } = useMemo(() => {
    const layers: { [k: string]: boolean } = {};
    // if (tab === Tab.VENUES) {
    layers.venuepoint = true;
    layers.venuelabel = true;
    //     layers.routepolyline = true;
    // } else if (tab === Tab.ROUTE) {
    layers.tourvenuepoint = true;
    layers.tourvenuelabel = true;
    layers.routepolyline = true;
    // }

    return layers;
  }, []);

  const venueFilter = useMemo(() => {
    const v = ["in", "_path", ...filteredVenues.map((v) => v.properties._path)];
    return v;
  }, [filteredVenues]);

  return (
    <>
      <div className="relative w-full h-full">
        {/* <div className="m-1 sm:hidden">
              <button onClick={() => setMobileToggle(!mobileToggle)}>
                <i className="fal fa-times text-3xl p-3"></i>
              </button>
            </div> */}
        <div className="z-10 absolute left-0 right-0 top-0 pointer-events-none mt-2 mr-2 ml-2 flex flex-row">
          <div className="flex-1 flex flex-row flex-wrap justify-between">
            <div
              className="text-center pointer-events-auto mb-1 hidden lg:block"
              style={{ width: 210 }}
            >
              <button
                className="flex flex-row items-center border border-black text-lightoffwhite justify-center bg-black paragraph button"
                onClick={() => setShowFilters(true)}
              >
                <span className="inline-flex items-center pr-2 text-lightoffwhite button-text">
                  Filters
                </span>
                <svg
                  className="inline-block"
                  width="13px"
                  viewBox="0 0 15 14.98"
                >
                  <g id="g3015">
                    <path
                      fill="#f4f3ee"
                      d="M14.94.41a.62.62,0,0,1-.15.75L9.54,6.4v7.9a.65.65,0,0,1-.41.63.86.86,0,0,1-.27.05.66.66,0,0,1-.48-.2L5.66,12.05a.62.62,0,0,1-.2-.48V6.4L.21,1.16A.62.62,0,0,1,.06.41.64.64,0,0,1,.69,0H14.31A.64.64,0,0,1,14.94.41Z"
                    />
                  </g>
                </svg>
              </button>
            </div>
            <div
              className="text-center pointer-events-auto mb-1 hidden lg:block"
              style={{ width: 210 }}
            >
              <div className="flex flex-row items-center border border-black text-black justify-center bg-lightoffwhite paragraph">
                <label className="inline-flex items-center p-2 text-black">
                  <input
                    type="checkbox"
                    className="form-checkbox h-5 w-4"
                    checked={isFilterMapMove}
                    onChange={(event) =>
                      setIsFilterMapMove(event.target.checked)
                    }
                  />
                  <span className="ml-2">Filter as map moves</span>
                </label>
              </div>
            </div>

            <div
              className="pointer-events-auto mb-1 relative"
              style={{ width: 250 }}
            >
              <Search
                placeholder="Search venue or location"
                onSelectPlace={(place: any) => {
                  // console.log('onSelectPlace', { place })
                  if (place && mapEl && mapEl.current && mapEl.current.map) {
                    if (place.type === "venue") {
                      const venue = venues.find(
                        (v) => place.id === v.properties._path
                      );

                      if (venue) {
                        setSelectedVenue(venue);
                      }
                    } else {
                      mapEl.current.map.easeTo({
                        center: {
                          lon: place.geometry.coordinates[0],
                          lat: place.geometry.coordinates[1],
                        },
                        zoom: 13,
                      });
                    }
                  }
                }}
                venues={venues}
              />
            </div>
          </div>

          <div
            className={
              "md:hidden pointer-events-auto ml-3 transition-opacity opacity-100"
            }
          >
            <button onClick={() => setMobileToggle(true)}>
              <i className="fal fa-bars text-3xl px-3 pb-3"></i>
            </button>
          </div>

          <Legend />
        </div>
        {/* <div className="z-10 absolute right-0 top-0 mt-2 mr-2" style={{ width: 250 }}>
                <Search placeholder="Search venue or location" onSelectPlace={(place: any) => {
                    // console.log('onSelectPlace', { place })
                    if (place && mapEl && mapEl.current && mapEl.current.map) {
                        if (place.type === 'venue') {
                            const venue = venues.find(v => place.id === v.properties._path);

                            if (venue) {
                                setSelectedVenue(venue);
                            }
                        } else {
                            mapEl.current.map.easeTo({
                                center: { lon: place.geometry.coordinates[0], lat: place.geometry.coordinates[1] },
                                zoom: 13
                            })
                        }
                    }
                }} venues={venues} />
            </div>

            <div className="z-10 absolute left-1/2 top-0 mt-2 mr-2 text-center" style={{ width: 210, marginLeft: -150 }}>
                <div className="flex flex-row items-center border border-black text-black justify-center bg-offwhite paragraph">
                    <label className="flex inline-flex items-center p-2 text-black">
                        <input type="checkbox" className="form-checkbox h-5 w-4" checked={isFilterMapMove} onChange={(event) => setIsFilterMapMove(event.target.checked)} /><span className="ml-2">Filter as map moves</span>
                    </label>
                </div>
            </div> */}

        <Map
          ref={mapEl}
          popups={popups}
          onClick={(e) => {
            if (e.features && e.features.length > 0) {
              if (e.features.length === 1) {
                const selectedFeature = e.features[0];
                const venue = venues.find(
                  (v) => selectedFeature.id === v.properties._path
                );

                if (venue) {
                  setSelectedVenue(venue);
                }
              } else {
                const venueOptions = (e.features as AppFeature[])
                  .map((selectedFeature) =>
                    venues.find(
                      (v) => selectedFeature.id === v.properties._path
                    )
                  )
                  .filter((v) => v !== undefined) as AppFeature[];
                setVenueOptions(venueOptions);
              }
            } else {
              setSelectedVenue(null);
            }
          }}
          onMapMove={(view) => setMapViewport(view)}
          initProps={{
            accessToken: MAPBOX_API_KEY,
            style: "mapbox://styles/musicnsw/cl1srcyyb001g15o5r7fxfm1r",
            bounds: [
              [140.86669921875, -37.6664292120906],
              [153.91845703125, -27.552590823339845],
            ],
            maxBounds: [
              [134.12109375, -44.43],
              [160.224609375, -20.14],
            ],
            attributionControl: false,
          }}
          sources={{
            venues: {
              type: "geojson",
              promoteId: "_path",
              data: {
                type: "FeatureCollection",
                features: venues || [],
              },
            },
            tourVenues: {
              type: "geojson",
              promoteId: "_path",
              data: {
                type: "FeatureCollection",
                features: tourVenues || [],
              },
            },
            route: {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: routeGeojson as any,
              },
            },
          }}
          layers={{
            routepolyline: {
              layer: {
                id: "routepolyline",
                type: "line",
                source: "route",
                paint: {
                  "line-width": 2,
                  "line-color": "#414042",
                  "line-dasharray": [1, 1],
                },
              },
            },
            // SBK - SHAPE & COLOUR VARIATION
            // venuepoint: {
            //     layer: {
            //         id: "venuepoint",
            //         type: "symbol",
            //         source: "venues",
            //         layout: {
            //             //'icon-image': ['concat', 'circle-', 'inactive-', '#45C394'],
            //             'icon-image': [
            //                 "match",
            //                 ["get", "venue_type"],
            //                 ["Cafe/Restaurant"],
            //                 "pink_star",
            //                 ["Club"],
            //                 "red_star",
            //                 ["Community space/hall"],
            //                 "orange_circle",
            //                 ["Dedicated live music venue"],
            //                 "green_star",
            //                 ["Pub/Hotel/Bar"],
            //                 "teal_diamond",
            //                 ["Theatre or Performing art space"],
            //                 "blue_clover",
            //                 "mountain"
            //               ],
            //             'icon-size': [
            //                 "interpolate",
            //                 ["linear"],
            //                 ["zoom"],
            //                 8, 0.25,
            //                 16, 1
            //             ],
            //             'icon-ignore-placement': true,
            //             'icon-allow-overlap': true,
            //         },
            //         paint: {
            //             'text-color': '#000000',
            //             'text-halo-blur': 1,
            //             'text-halo-color': "rgba(255,255,255,1)",
            //             'text-halo-width': 3
            //         }
            //     },
            //     filters: venueFilter
            // },
            // SBK - COLOUR VARIATION - CLOVER
            // venuepoint: {
            //     layer: {
            //         id: "venuepoint",
            //         type: "symbol",
            //         source: "venues",
            //         layout: {
            //             //'icon-image': ['concat', 'circle-', 'inactive-', '#45C394'],
            //             'icon-image': [
            //                 "match",
            //                 ["get", "venue_type"],
            //                 ["Cafe/Restaurant"],
            //                 "pink_clover_sbk",
            //                 ["Club"],
            //                 "red_clover_sbk",
            //                 ["Community space/hall"],
            //                 "orange_clover_sbk",
            //                 ["Dedicated live music venue"],
            //                 "green_clover_sbk",
            //                 ["Pub/Hotel/Bar"],
            //                 "teal_clover_sbk",
            //                 ["Theatre or Performing art space"],
            //                 "blue_clover",
            //                 "mountain"
            //               ],
            //             'icon-size': [
            //                 "interpolate",
            //                 ["linear"],
            //                 ["zoom"],
            //                 8, 0.15,
            //                 16, 0.3
            //             ],
            //             'icon-ignore-placement': true,
            //             'icon-allow-overlap': true,
            //         },
            //         paint: {
            //             'text-color': '#000000',
            //             'text-halo-blur': 1,
            //             'text-halo-color': "rgba(255,255,255,1)",
            //             'text-halo-width': 3
            //         }
            //     },
            //     filters: venueFilter
            // },
            // SBK - COLOUR VARIATION - STAR
            venuepoint: {
              before: "beneath-labels-sbk",
              layer: {
                id: "venuepoint",
                type: "symbol",
                source: "venues",
                layout: {
                  //'icon-image': ['concat', 'circle-', 'inactive-', '#45C394'],
                  "icon-image": [
                    "match",
                    ["get", "venue_type"],
                    ["Cafe/Restaurant"],
                    "venue_pink",
                    ["Club"],
                    "venue_teal",
                    ["Community space/hall"],
                    "venue_red",
                    ["Dedicated live music venue"],
                    "venue_green",
                    ["Festival"],
                    "venue_yellow",
                    ["Pub/Hotel/Bar"],
                    "venue_blue",
                    ["Theatre or Performing art space"],
                    "venue_orange",
                    "venue_grey",
                  ],
                  "icon-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    0.25,
                    16,
                    0.5,
                  ],
                  // 'icon-size': [
                  //     "interpolate",
                  //     ["linear"],
                  //     ["zoom"],
                  //     8,
                  //     [
                  //       "match",
                  //       [
                  //         "get",
                  //         "capacity_category"
                  //       ],
                  //       ["< 120"],
                  //       0.15,
                  //       ["120 - 249"],
                  //       0.2,
                  //       ["250 - 499"],
                  //       0.25,
                  //       ["500 - 999"],
                  //       0.3,
                  //       ["1,000 +"],
                  //       0.35,
                  //       0.2
                  //     ],
                  //     16,
                  //     [
                  //       "match",
                  //       [
                  //         "get",
                  //         "capacity_category"
                  //       ],
                  //       ["< 120"],
                  //       0.3,
                  //       ["120 - 249"],
                  //       0.35,
                  //       ["250 - 499"],
                  //       0.4,
                  //       ["500 - 999"],
                  //       0.45,
                  //       ["1,000 +"],
                  //       0.5,
                  //       0.5
                  //     ]
                  //   ],
                  "icon-ignore-placement": true,
                  "icon-allow-overlap": true,
                  "text-allow-overlap": true,
                  "text-ignore-placement": false,
                },
                paint: {
                  "text-color": "#000000",
                  "text-halo-blur": 1,
                  "text-halo-color": "rgba(255,255,255,1)",
                  "text-halo-width": 3,
                },
              },
              filters: venueFilter,
            },
            venuelabel: {
              layer: {
                id: "venuelabel",
                type: "symbol",
                source: "venues",
                layout: {
                  "icon-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    0.5,
                    16,
                    1,
                  ],
                  "icon-ignore-placement": true,
                  "icon-allow-overlap": true,
                  "text-optional": true,

                  "text-line-height": 1.1,
                  "text-field": [
                    "format",
                    ["get", "title"],
                    {},
                    // '\n', {},
                    // ['get', 'venue_type_a'], {
                    //     'font-scale': 0.9,
                    //     'text-color': '#444'
                    // }
                    // ['case', ['<=', ['get', 'priority'], 4], '\n', ''], {},
                    // ['case', ['<=', ['get', 'priority'], 4], ['get', 'availability'], ''], {
                    //   'font-scale': 0.9,
                    //   'text-color': '#444'
                    // }
                  ],
                  "text-variable-anchor": ["left", "right"],
                  "text-justify": "auto" as any,
                  "text-radial-offset": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    0.5,
                    16,
                    0.8,
                  ],
                  "text-size": 12,
                  "text-ignore-placement": true,
                  "text-max-width": 9,
                },
                paint: {
                  "text-color": "#000000",
                  "text-halo-blur": 1,
                  "text-halo-color": "rgba(255,255,255,1)",
                  "text-halo-width": 1,
                  "text-opacity": ["step", ["zoom"], 0, 12, 0],
                },
              },
              filters: venueFilter,
            },

            tourvenuepoint: {
              layer: {
                id: "tourvenuepoint",
                type: "symbol",
                source: "tourVenues",
                layout: {
                  "icon-image": "tour_black",
                  "icon-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    5,
                    0.3,
                    13,
                    0.6,
                  ],
                  "icon-ignore-placement": true,
                  "icon-allow-overlap": true,
                },
                paint: {
                  "text-color": "#000000",
                  "text-halo-blur": 1,
                  "text-halo-color": "rgba(255,255,255,1)",
                  "text-halo-width": 3,
                },
              },
            },
            tourvenuelabel: {
              layer: {
                id: "tourvenuelabel",
                type: "symbol",
                source: "tourVenues",
                layout: {
                  "icon-size": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    8,
                    0.75,
                    16,
                    2,
                  ],
                  "icon-ignore-placement": false,
                  "icon-allow-overlap": false,
                  "text-optional": true,

                  "text-line-height": 1.1,
                  "text-field": [
                    "format",
                    ["get", "title"],
                    {},
                    // ['case', ['<=', ['get', 'priority'], 4], '\n', ''], {},
                    // ['case', ['<=', ['get', 'priority'], 4], ['get', 'availability'], ''], {
                    //   'font-scale': 0.9,
                    //   'text-color': '#444'
                    // }
                  ],
                  "text-variable-anchor": ["left", "right"],
                  "text-justify": "auto" as any,
                  "text-radial-offset": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    5,
                    1.2,
                    16,
                    3,
                  ],
                  "text-size": 12,
                  "text-ignore-placement": false,
                  "text-max-width": 9,
                  "text-font": ["DM Sans Medium", "Arial Unicode MS Regular"],
                },
                paint: {
                  "text-color": "#000000",
                  "text-halo-blur": 0,
                  "text-halo-color": "rgba(255,255,255,1)",
                  "text-halo-width": 0,
                  // 'text-opacity': ["step", ["zoom"], 0, 12, 1]
                },
              },
            },
          }}
          containerStyle={{ width: "100%", height: "100%" }}
          visibleLayers={visibleLayers}
        />
      </div>
    </>
  );
};
